// @flow
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { Box } from '@material-ui/core';

import Progress from './Progress';
import { useStores } from '../../stores';
import appStyles from '../../../../config/appStyles.js';
import { loadSnakeAddress } from '../../../../utils/snakeAddress';

const useStyles = makeStyles((theme) => appStyles.pickupPointMap(theme));

// a module script that never fires load/error would leave the map hanging forever
const LOAD_TIMEOUT = 15000;

// The provider (DPD) mounts its own iframe in here and reports the chosen point.
const PickupPointMap = observer(({ className, onSelect }) => {
  const classes = useStyles();
  const container = useRef(null);
  const widget = useRef(null);

  const { t, i18n } = useTranslation(['components']);
  const { uiStore } = useStores();

  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const fail = (err) => {
      console.log('pickup point map err', err);
      if (!cancelled) {
        setStatus('error');
      }
    };
    const timeout = setTimeout(
      () => fail(new Error('timeout loading ' + process.env.REACT_APP_SNAKE_ADDRESS_URL)),
      LOAD_TIMEOUT,
    );

    if (container.current) {
      container.current.scrollIntoView({ block: 'nearest' });
    }

    loadSnakeAddress()
      .then((SnakeAddress) => {
        clearTimeout(timeout);
        if (cancelled || !container.current) {
          return;
        }
        const api = SnakeAddress({
          lang: i18n.language,
          country: uiStore.country,
        });
        widget.current = api.ooh({
          container: container.current,
          widgetParams: { hideCloseButton: 'true' },
          onSelect,
        });
        setStatus('ready');
      })
      .catch((err) => {
        clearTimeout(timeout);
        fail(err);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (widget.current) {
        widget.current.destroy();
        widget.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={clsx(classes.root, className)}>
      {status === 'loading' && <Progress variant="medium" />}
      {status === 'error' && (
        <Box className={classes.error}>
          {t('components:ChangeAddressModal.pickupPointError')}
        </Box>
      )}
      <div className={classes.widget} ref={container} />
    </Box>
  );
});

export default PickupPointMap;
